<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('employeeMission.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          { text: $t('employeeMission.pageTitle'), active: true }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button outline color="info" @click.prevent="addNew">
            {{ $t('add_new') }}
          </ts-button>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input
              v-tooltip="$t('employeeMission.searchPlaceholder')"
              v-model="search"
              :placeholder="$t('employeeMission.searchPlaceholder')"
              style="width: 250px"
              class="inline-block"
            />
          </div>
        </div>
      </ts-card-wrapper>

      <div class="overflow-auto">
        <Table
          :columns="columns"
          :data="resources"
          size="small"
          :loading="loading"
          max-height="700"
          stripe
        >
          <template slot-scope="{ row }" slot="meal_allowance">
            {{ row.meal_allowance || 0 }}
          </template>
          <template slot-scope="{ row }" slot="accommodation_allowance">
            {{ row.accommodation_allowance || 0 }}
          </template>
          <template slot-scope="{ row }" slot="day_return_allowance">
            {{ row.day_return_allowance || 0 }}
          </template>
          <template slot-scope="{ row }" slot="advance_amount">
            {{ row.advance_amount || 0 }}
          </template>
          <template slot-scope="{ row }" slot="action">
            <a
              href="#"
              v-tooltip="$t('edit')"
              class="text-primary mr-2"
              @click.prevent="edit(row)"
            >
              <i class="fas fa-edit"></i>
            </a>

            <a
              href="#"
              v-tooltip="$t('employeeMission.advanceRequest')"
              class="text-success mr-2"
              @click.prevent="openAdvance(row)"
            >
              <i class="fas fa-dollar-sign"></i>
            </a>
            <a-popconfirm
              placement="leftTop"
              :title="$t('are_you_sure_to_delete')"
              :ok-text="$t('yes')"
              :cancel-text="$t('no')"
              @confirm="deleteRecord(row)"
            >
              <a href="#" :disabled="row._deleting">
                <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
                <i class="fa fa-trash text-red" v-else></i>
              </a>
            </a-popconfirm>
          </template>
        </Table>
      </div>

      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData" />
      </div>

      <!-- Edit form modal -->
      <a-modal
        v-if="showForm"
        v-model="showForm"
        :title="$t('employeeMission.pageTitle')"
        centered
        @cancel="clearEdit"
        :zIndex="1020"
        :footer="null"
        :width="800"
      >
        <from-action
          @cancel="() => (showForm = false)"
          @fetchData="fetchData"
        />
      </a-modal>
      <!-- Advance request modal -->
      <a-modal
        v-if="showAdvanceForm"
        v-model="showAdvanceForm"
        :title="$t('employeeMission.advanceRequest')"
        centered
        :zIndex="1020"
        :footer="null"
        :width="500"
        @cancel="showAdvanceForm = false"
      >
        <advance-add-form
          :mission="selectedMission"
          @close="showAdvanceForm = false"
          @updated="fetchData"
        />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FromAction from './form'
import expandRow from './table-expand.vue'
import AdvanceAddForm from './advance-add-form.vue'

export default {
  name: 'EmployeeMission',
  components: {
    FromAction,
    AdvanceAddForm
  },
  data() {
    return {
      loading: false,
      showForm: false,
      showAdvanceForm: false,
      selectedMission: null
    }
  },
  computed: {
    ...mapState('mission/employeeMission', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.mission.employeeMission.search
      },
      set(newValue) {
        this.$store.commit('mission/employeeMission/SET_SEARCH', newValue)
        this.$store.commit('mission/employeeMission/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          type: 'expand',
          render: (h, params) => {
            return h(expandRow, {
              props: { row: params.row },
              on: { updated: () => this.fetchData() }
            })
          },
          width: 50
        },
        {
          title: this.$t('employeeMission.employee'),
          key: 'employee_name',
          minWidth: 150,
          align: 'center'
        },
        {
          title: this.$t('employeeMission.missionType'),
          key: 'mission_type_name',
          minWidth: 200,
          align: 'center'
        },
        {
          title: this.$t('employeeMission.missionTitle'),
          key: 'mission_title',
          minWidth: 200,
          align: 'center'
        },
        {
          title: this.$t('employeeMission.startDate'),
          key: 'start_date',
          minWidth: 120,
          align: 'center'
        },
        {
          title: this.$t('employeeMission.endDate'),
          key: 'end_date',
          minWidth: 120,
          align: 'center'
        },
        {
          title: this.$t('employeeMission.totalDays'),
          key: 'total_day',
          minWidth: 100,
          align: 'center'
        },
        {
          title: this.$t('employeeMission.mealAllowance'),
          slot: 'meal_allowance',
          minWidth: 150,
          align: 'right'
        },
        {
          title: this.$t('employeeMission.accommodationAllowance'),
          slot: 'accommodation_allowance',
          minWidth: 200,
          align: 'right'
        },
        {
          title: this.$t('employeeMission.dayReturnAllowance'),
          slot: 'day_return_allowance',
          minWidth: 200,
          align: 'right'
        },
        {
          title: this.$t('employeeMission.advanceAmount'),
          slot: 'advance_amount',
          minWidth: 150,
          align: 'right'
        },
        {
          title: this.$t('employeeMission.approvedBy'),
          key: 'approved_by_name',
          minWidth: 150,
          align: 'center'
        },
        {
          title: this.$t('employeeMission.assignedBy'),
          key: 'assigned_by_name',
          minWidth: 150,
          align: 'center'
        },
        {
          title: this.$t('actions'),
          slot: 'action',
          align: 'center',
          width: 120,
          fixed: 'right'
        }
      ]
    }
  },
  methods: {
    async fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('mission/employeeMission/fetch', attributes)
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.$store.commit('mission/employeeMission/SET_EDIT_DATA', {})
      this.$nextTick(() => {
        this.showForm = true
      })
    },
    edit(record) {
      this.$store.commit('mission/employeeMission/SET_EDIT_DATA', record)
      this.$nextTick(() => {
        this.showForm = true
      })
    },
    clearEdit() {
      this.$store.commit('mission/employeeMission/SET_EDIT_DATA', {})
      this.showForm = false
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('mission/employeeMission/destroy', record.id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    openAdvance(record) {
      this.selectedMission = record
      this.$nextTick(() => {
        this.showAdvanceForm = true
      })
    }
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('mission/employeeMission/RESET_STATE')
    next()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  }
}
</script>
